import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { getThemeColors } from '../../utils/theme';
import API_ENDPOINTS from '../../config';
import { authenticatedFetch } from '../../utils/api';
import logger from '../../utils/logger';

export default function AdminUsers() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    verified: '',
    companyId: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.role && { role: filters.role }),
        ...(filters.verified !== '' && { verified: filters.verified }),
        ...(filters.companyId && { companyId: filters.companyId })
      });

      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/users?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      });
    } catch (error) {
      logger.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'member',
      isVerified: user.isVerified || false,
      companyId: user.companyId || ''
    });
    setEditMode(true);
  };

  const handleUpdateUser = async () => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }

      // Refresh users list
      fetchUsers(pagination?.page || 1);
      setEditMode(false);
      setSelectedUser(null);
    } catch (error) {
      logger.error('Error updating user:', error);
      if (error.message !== 'Unauthorized - redirected to login') {
        alert(error.message);
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('admin.users.deleteConfirm'))) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      // Refresh users list
      fetchUsers(pagination?.page || 1);
    } catch (error) {
      logger.error('Error deleting user:', error);
      if (error.message !== 'Unauthorized - redirected to login') {
        alert(t('admin.users.failedToLoad'));
      }
    }
  };

  const formatDate = (dateString) => {
    const localeMap = {
      'sr': 'sr-RS',
      'zh': 'zh-CN',
      'en': 'en-US'
    };
    const locale = localeMap[i18n.language] || 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && users.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '18px', color: colors.textSecondary }}>{t('admin.users.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, margin: '0 0 0.5rem 0' }}>
          {t('admin.users.title')}
        </h1>
        <p style={{ color: colors.textSecondary, margin: 0 }}>
          {t('admin.users.subtitle')}
        </p>
      </div>

      {/* Search and Filters */}
      <div style={{
        background: colors.card,
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: colors.shadow,
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.users.searchLabel')}
            </label>
            <input
              type="text"
              placeholder={t('admin.users.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: colors.background,
                color: colors.text
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.users.filterByRole')}
            </label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: colors.background,
                color: colors.text
              }}
            >
              <option value="">{t('admin.users.allRoles')}</option>
              <option value="admin">{t('admin.users.roleAdmin')}</option>
              <option value="member">{t('admin.users.roleMember')}</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.users.filterByVerified')}
            </label>
            <select
              value={filters.verified}
              onChange={(e) => setFilters({ ...filters, verified: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: colors.background,
                color: colors.text
              }}
            >
              <option value="">{t('admin.users.allUsers')}</option>
              <option value="true">{t('admin.users.verified')}</option>
              <option value="false">{t('admin.users.unverified')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div style={{
        background: colors.card,
        borderRadius: '12px',
        boxShadow: colors.shadow,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundLight }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.users.user')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.users.email')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.users.role')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('common.status')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.users.registrationDate')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'center', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.users.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id} style={{ 
                  borderBottom: index < users.length - 1 ? `1px solid ${colors.border}` : 'none',
                  background: index % 2 === 0 ? colors.card : colors.backgroundLight
                }}>
                  <td style={{ padding: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: 500, color: colors.text }}>
                        {user.firstName} {user.lastName}
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {user.Company?.name}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '14px', color: colors.text }}>
                    {user.email}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 500,
                      background: user.role === 'admin' ? '#fee2e2' : '#dbeafe',
                      color: user.role === 'admin' ? '#dc2626' : '#2563eb'
                    }}>
                      {user.role === 'admin' ? t('admin.users.roleAdmin') : t('admin.users.roleMember')}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 500,
                      background: user.isVerified ? '#d1fae5' : '#fef3c7',
                      color: user.isVerified ? '#059669' : '#d97706'
                    }}>
                      {user.isVerified ? t('admin.users.verified') : t('admin.users.unverified')}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '14px', color: colors.textSecondary }}>
                    {formatDate(user.createdAt)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleEditUser(user)}
                        style={{
                          padding: '6px 12px',
                          background: colors.primary,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.primaryHover}
                        onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        style={{
                          padding: '6px 12px',
                          background: colors.error,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = isDark ? '#ef4444' : '#b91c1c'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = colors.error}
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination?.pages > 1 && (
          <div style={{ 
            padding: '1rem', 
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>
              {t('admin.users.showingUsers', {
                from: ((pagination.page - 1) * pagination.limit) + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total
              })}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => fetchUsers(pagination.page - 1)}
                disabled={pagination.page <= 1}
                style={{
                  padding: '8px 12px',
                  background: pagination.page <= 1 ? colors.backgroundLight : colors.primary,
                  color: pagination.page <= 1 ? colors.textLight : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                {t('common.previous')}
              </button>
              <span style={{ padding: '8px 12px', fontSize: '14px', color: colors.text }}>
                {t('admin.users.pageOf', { page: pagination.page, pages: pagination.pages })}
              </span>
              <button
                onClick={() => fetchUsers(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                style={{
                  padding: '8px 12px',
                  background: pagination.page >= pagination.pages ? colors.backgroundLight : colors.primary,
                  color: pagination.page >= pagination.pages ? colors.textLight : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer'
                }}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editMode && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: colors.card,
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: 600, color: colors.text }}>
              {t('admin.users.editUser')}
            </h3>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.users.firstName')}
                </label>
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.users.lastName')}
                </label>
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.users.email')}
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.users.phone')}
                </label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.users.role')}
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                >
                  <option value="member">{t('admin.users.roleMember')}</option>
                  <option value="admin">{t('admin.users.roleAdmin')}</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="isVerified"
                  checked={editForm.isVerified}
                  onChange={(e) => setEditForm({ ...editForm, isVerified: e.target.checked })}
                />
                <label htmlFor="isVerified" style={{ fontSize: '14px', color: colors.text }}>
                  {t('admin.users.verifiedUser')}
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '2rem' }}>
              <button
                onClick={handleUpdateUser}
                style={{
                  padding: '12px 24px',
                  background: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                {t('admin.users.saveChanges')}
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  setSelectedUser(null);
                }}
                style={{
                  padding: '12px 24px',
                  background: colors.backgroundLight,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                {t('admin.users.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 